/**
 * @fileoverview Interceptores de axios para la sesión de CR Auth en las llamadas a ZS Backend por Apigee.
 * El 401 que genera la propia Apigee llega sin cabeceras CORS y el navegador no lo deja leer:
 * por eso la renovación es preventiva (antes de cada request) y el reintento solo cubre un 401 legible.
 * @module utils/authInterceptors
 */

import environment from './environment'

const ABSOLUTE_URL = /^https?:\/\//i

/**
 * Indica si la llamada va a ZS Backend: rutas relativas (baseURL = API_URL) o que empiezan con API_URL/API_URL_V2.
 * @param {Object} config - Config de axios
 * @param {string[]} [zsBackendUrls]
 * @returns {boolean}
 */
export function isZsBackendRequest (config, zsBackendUrls = environment.getZsBackendUrls()) {
  const url = (config && config.url) || ''
  if (!ABSOLUTE_URL.test(url)) {
    return true
  }
  return zsBackendUrls.some(base => url.indexOf(base) === 0)
}

/**
 * Interceptor de request: con sesión, garantiza un access token vigente antes de llamar a ZS Backend.
 * Si la sesión no se puede renovar, la request se rechaza sin salir.
 * @param {Object} store - Store de Vuex
 * @returns {function(Object): Promise<Object>}
 */
export function createSessionRequestInterceptor (store) {
  return function (config) {
    const userState = store.state.user
    if (!isZsBackendRequest(config) || (!userState.session && !userState.user)) {
      return Promise.resolve(config)
    }
    return store.dispatch('user/ensureSession').then(() => config)
  }
}

/**
 * Devuelve el body a objeto: axios ya lo serializó y el interceptor global agrega `origen` sobre el objeto.
 * @param {Object} config
 * @returns {Object}
 */
function restoreRequestData (config) {
  if (typeof config.data === 'string') {
    try {
      config.data = JSON.parse(config.data)
    } catch (e) {
      // Body que no es JSON: se reenvía tal cual
    }
  }
  return config
}

/**
 * Interceptor de error de response: ante un 401 legible de ZS Backend renueva la sesión y reintenta una sola vez.
 * Si el reintento vuelve a dar 401, la sesión vence. Los errores sin status (red) no se reintentan.
 * @param {Object} store - Store de Vuex
 * @param {function(Object): Promise} http - Instancia de axios para reintentar
 * @returns {function(Object): Promise}
 */
export function createUnauthorizedRetryInterceptor (store, http) {
  return function (error) {
    const config = error && error.config
    const status = error && error.response && error.response.status
    if (status !== 401 || !config || config.__sessionRetry || !store.state.user.session || !isZsBackendRequest(config)) {
      return Promise.reject(error)
    }
    config.__sessionRetry = true
    return store.dispatch('user/ensureSession', { force: true })
      .then(() => http(restoreRequestData(config)))
      .catch(retryError => {
        const retryStatus = retryError && retryError.response && retryError.response.status
        if (retryStatus === 401 && store.state.user.session) {
          store.commit('user/expireSession')
        }
        throw retryError
      })
  }
}

/**
 * Suscriptor del store: cuando la sesión vence, avisa una sola vez con el modal existente y vuelve al inicio.
 * @param {{swal: Function, router: Object, storage: Object}} deps
 * @returns {function(Object): void}
 */
export function createSessionExpiredNotifier ({ swal, router, storage }) {
  let notifying = false
  return function (mutation) {
    if (mutation.type !== 'user/expireSession' || notifying) {
      return
    }
    notifying = true
    swal({
      titleText: 'Tu sesión ha terminado',
      text: 'Por favor, ingresa nuevamente.',
      confirmButtonText: 'Salir'
    }).then(() => {
      notifying = false
      storage.removeItem('jwt')
      router.push('/').catch(() => { })
    })
  }
}



// WEBPACK FOOTER //
// ./src/utils/authInterceptors.js